'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useT } from '@/phrase/hooks/useT'

type LogoTickerProps = {
  /** Array of image URLs */
  logos: string[]
  /** Height of each logo row in px (container height will match) */
  height?: number // default 64
  /** Gap between logos in px */
  gap?: number // default 24
  /** Scrolling speed in pixels per second */
  speed?: number // default 60 px/s
  /** Scroll direction */
  direction?: 'left' | 'right' // default "left"
  /** Pause animation when hovering */
  pauseOnHover?: boolean // default true
  /** Optional className for outer wrapper */
  className?: string
  /** Optional alt text builder; fallback uses index */
  getAlt?: (index: number, src: string) => string
  /** Optional fixed item width (px). If not set, items size to their intrinsic image width (object-contain) */
  itemWidth?: number
}

export default function LogoTicker({
  logos,
  height = 64,
  gap = 24,
  speed = 60,
  direction = 'left',
  pauseOnHover = true,
  className = '',
  getAlt,
  itemWidth,
}: LogoTickerProps) {
  const trackRef = useRef<HTMLUListElement>(null)
  const [duration, setDuration] = useState<number>(20) // seconds
  const [distance, setDistance] = useState<number>(0) // px to travel per loop
  const [repeatK, setRepeatK] = useState<number>(1) // how many times we repeat `logos` per logical unit

  const t = useT()

  // const [dims, setDims] = useState<{ w: number; h: number }[]>(() =>
  //   Array.from({ length: logos.length }, () => ({ w: 0, h: 0 })),
  // )

  // useEffect(() => {
  //   setDims(Array.from({ length: logos.length }, () => ({ w: 0, h: 0 })))
  // }, [logos])

  const fade = 64 // px fade on both left and right edges

  // We duplicate the list to create a seamless loop
  const items = useMemo(() => {
    const out: string[] = []
    const total = Math.max(1, repeatK * 2)
    for (let i = 0; i < total; i++) out.push(...logos)
    return out
  }, [logos, repeatK])

  useEffect(() => {
    const el = trackRef.current
    if (!el) return

    const parent = el.parentElement as HTMLElement | null

    const measure = () => {
      // If we currently render 2 * repeatK copies, a single base sequence width is:
      const renderedCopies = Math.max(1, repeatK * 2)
      const totalWidth = el.scrollWidth
      const baseWidth = totalWidth / renderedCopies // width of one `logos` sequence with current gap

      // Guard: if baseWidth is 0 (images not laid out yet), try again later
      if (!isFinite(baseWidth) || baseWidth <= 0) return

      const containerWidth = parent?.clientWidth ?? 0

      // We want one logical unit (repeatK * baseWidth) to be strictly wider than the container
      const neededK = Math.max(1, Math.ceil(containerWidth / baseWidth) + 1)

      if (neededK !== repeatK) {
        // Update repeatK then re-measure on next layout
        setRepeatK(neededK)
        return
      }

      const unitWidth = baseWidth * neededK
      const pxPerSecond = Math.max(10, speed)
      const seconds = unitWidth / pxPerSecond

      setDistance(unitWidth)
      setDuration(seconds)
    }

    // Initial measure
    measure()

    // Re-measure on resize and when images load
    const ro = new ResizeObserver(measure)
    ro.observe(parent || el)

    const imgs = Array.from(el.querySelectorAll('img'))
    imgs.forEach((img) => {
      if (img.complete) return
      img.addEventListener('load', measure, { once: true })
      img.addEventListener('error', measure, { once: true })
    })

    return () => {
      ro.disconnect()
    }
  }, [speed, logos, repeatK])

  return (
    <div className="flex flex-col items-center gap-4 mt-10 mb-4">
      <h3 className="text-center  text-base font-semibold text-[#A6A7B5] z-40 uppercase">
        {t('as_seen_in_title')}
      </h3>
      <div
        className={[
          'relative w-full overflow-hidden bg-transparent ',
          className,
        ].join(' ')}
        style={{
          height,
          WebkitMaskImage: `linear-gradient(to right, transparent 0, black ${fade}px, black calc(100% - ${fade}px), transparent 100%)`,
          maskImage: `linear-gradient(to right, transparent 0, black ${fade}px, black calc(100% - ${fade}px), transparent 100%)`,
        }}
        aria-label="Partner logos"
        role="region"
      >
        {/* The animated track */}
        <ul
          ref={trackRef}
          className="flex items-center m-0 p-0 list-none"
          style={
            {
              gap: `${gap}px`,
              // CSS variables to control animation from JS
              // We move the track by the exact measured pixel distance of one copy.
              ['--marquee-duration']: `${duration}s`,
              ['--marquee-direction']:
                direction === 'left' ? 'forwards' : 'reverse',
              ['--marquee-distance']: `${distance}px`,
              willChange: 'transform',
              animation: 'logo-marquee var(--marquee-duration) linear infinite',
              animationDirection: 'var(--marquee-direction)',
              // Improve smoothness
              transform: 'translate3d(0,0,0)',
              backfaceVisibility: 'hidden',
              WebkitFontSmoothing: 'antialiased',
            } as React.CSSProperties
          }
          onMouseEnter={(e) => {
            if (!pauseOnHover) return
            e.currentTarget.style.animationPlayState = 'paused'
          }}
          onMouseLeave={(e) => {
            if (!pauseOnHover) return
            e.currentTarget.style.animationPlayState = 'running'
          }}
        >
          {items.map((src, i) => (
            <li
              key={`${i}-${src}`}
              className="flex-shrink-0"
              style={{
                height,
                width: itemWidth ? `${itemWidth}px` : 'auto',
              }}
            >
              {/* eslint-disable-next-line no-restricted-syntax */}
              <img
                src={src}
                alt={
                  getAlt
                    ? getAlt(i % logos.length, src)
                    : `logo-${i % logos.length}`
                }
                loading="lazy"
                // onLoad={(event) => {
                //   const img = event.currentTarget
                //   const w = img.naturalWidth
                //   const h = img.naturalHeight
                //   const idx = i % logos.length
                //   if (w && h && (dims[idx]?.w !== w || dims[idx]?.h !== h)) {
                //     setDims((prev) => {
                //       const next = [...prev]
                //       next[idx] = { w, h }
                //       return next
                //     })
                //   }
                // }}
                style={{
                  display: 'block',
                  height: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
